import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
  Database as DatabaseIcon,
  FileCode2 as NamedQueryIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Braces as DataPointIcon
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { plantQueryHref } from '@atbash-labs/plantview-links'
import {
  buildQueryFolderTree,
  useGetPlantQueriesQuery,
  type PlantQueryFolderNode,
  type PlantQuerySummary
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table'
import { Empty } from '../../components/primitives/Empty'
import { designPixelsToRem } from '../../lib/ui-density'

/**
 * `/plant/queries` — Queries tab landing.
 *
 * Surfaces the two database-query flavors the ontology carries:
 *   - `:NamedQuery` nodes (Ignition named queries), organized into a
 *     collapsible folder tree by project + `folder_path`, and
 *   - query-flavored `:Tag` nodes (`tag_system='db'` or
 *     `ignition_tag_type='query'`) — surfaced as "Query data points"
 *     because they include registered `db://` data points and Ignition
 *     query tags.
 *
 * Clicking either opens the detail page with the full SQL and a
 * "register as data point" affordance.
 */
const QueriesPage = (): React.ReactElement => {
  const { data, isLoading, isError } = useGetPlantQueriesQuery()
  const queries = React.useMemo(() => data ?? [], [data])
  const named = React.useMemo(() => queries.filter((q) => q.kind === 'named_query'), [queries])
  const dataPoints = React.useMemo(() => queries.filter((q) => q.kind === 'query_tag'), [queries])
  const tree = React.useMemo(() => buildQueryFolderTree(named), [named])

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header>
        <h1 className="m-0 text-2xl font-semibold text-ink">Queries</h1>
        <p className="mt-1 text-sm text-ink-3">{subtitle(queries.length)}</p>
      </header>

      {isError ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Couldn't load queries"
            hint="Check the proxy is reachable and try again."
            icon={WarningCircleIcon}
          />
        </div>
      ) : isLoading && queries.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <div className="text-sm text-ink-3">Loading queries…</div>
        </div>
      ) : queries.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="No queries yet"
            hint="Ingest an Ignition gateway with named queries, or register a db:// data point."
            icon={DatabaseIcon}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-card">
          {named.length > 0 && (
            <section>
              <SectionHeader
                label="Named queries"
                subtitle={`${named.length} from ingested gateways`}
              />
              <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
                {tree.map((root) => (
                  <QueryFolder key={root.path} node={root} depth={0} isProjectRoot />
                ))}
              </div>
            </section>
          )}
          {dataPoints.length > 0 && (
            <section>
              <SectionHeader
                label="Query data points"
                subtitle={`${dataPoints.length} registered db:// data point${dataPoints.length === 1 ? '' : 's'} & query-sourced tag${dataPoints.length === 1 ? '' : 's'}`}
              />
              <DataPointsTable queries={dataPoints} />
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      <span className="text-xs text-ink-3">{subtitle}</span>
    </div>
  )
}

/** Total leaf-query count under a folder node (recursive). */
function folderQueryCount(node: PlantQueryFolderNode): number {
  return node.queries.length + node.folders.reduce((acc, f) => acc + folderQueryCount(f), 0)
}

/** One collapsible folder in the named-query tree (project root or subfolder). */
function QueryFolder({
  node,
  depth,
  isProjectRoot = false
}: {
  node: PlantQueryFolderNode
  depth: number
  isProjectRoot?: boolean
}): React.ReactElement {
  const [open, setOpen] = React.useState(false)
  const count = folderQueryCount(node)
  const indent = depth * 16

  return (
    <div className={depth === 0 ? 'border-t first:border-t-0' : undefined}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-accent hover:text-accent-foreground"
        style={{ paddingLeft: designPixelsToRem(12 + indent) }}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDownIcon size={14} className="shrink-0 text-ink-4" aria-hidden />
        ) : (
          <ChevronRightIcon size={14} className="shrink-0 text-ink-4" aria-hidden />
        )}
        {open ? (
          <FolderOpenIcon size={15} className="shrink-0 text-brand" aria-hidden />
        ) : (
          <FolderIcon size={15} className="shrink-0 text-brand" aria-hidden />
        )}
        <span
          className={`min-w-0 truncate text-sm ${isProjectRoot ? 'font-semibold text-ink' : 'font-medium text-ink'}`}
        >
          {node.name}
        </span>
        <span className="ml-auto shrink-0 text-xs text-ink-4">{count}</span>
      </button>
      {open && (
        <div>
          {node.folders.map((child) => (
            <QueryFolder key={child.path} node={child} depth={depth + 1} />
          ))}
          {node.queries.map((query) => (
            <QueryLeafRow key={query.id} query={query} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

/** A single named query as a tree leaf row. */
function QueryLeafRow({
  query,
  depth
}: {
  query: PlantQuerySummary
  depth: number
}): React.ReactElement {
  const indent = depth * 16
  return (
    <Link
      to={plantQueryHref(query.id)}
      className="flex items-center gap-2 px-3 py-1.5 text-sm no-underline transition-colors hover:bg-accent hover:text-accent-foreground"
      style={{ paddingLeft: designPixelsToRem(12 + indent + 20) }}
    >
      <NamedQueryIcon size={14} className="shrink-0 text-ink-4" aria-hidden />
      <span className="min-w-0 truncate font-medium text-ink">{query.queryName ?? query.name}</span>
      {query.queryText && (
        <span className="hidden min-w-0 flex-1 truncate font-mono text-11 text-ink-4 sm:block">
          {previewSql(query.queryText)}
        </span>
      )}
      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        {query.databaseName && (
          <Badge variant="secondary" className="inline-flex items-center gap-1">
            <DatabaseIcon size={10} aria-hidden />
            {query.databaseName}
          </Badge>
        )}
        {query.tableCount > 0 && (
          <Badge variant="outline">
            {query.tableCount} {query.tableCount === 1 ? 'table' : 'tables'}
          </Badge>
        )}
      </span>
    </Link>
  )
}

/** Query data points (registered `db://` data points / query-sourced tags) as a table. */
function DataPointsTable({ queries }: { queries: PlantQuerySummary[] }): React.ReactElement {
  const navigate = useNavigate()
  return (
    <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[24%]">Name</TableHead>
            <TableHead>Query</TableHead>
            <TableHead className="w-[18%]">Database</TableHead>
            <TableHead className="w-[12%]">Type</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {queries.map((query) => (
            <TableRow
              key={query.id}
              className="cursor-pointer"
              onClick={() => navigate(plantQueryHref(query.id))}
            >
              <TableCell className="max-w-0">
                <Link
                  to={plantQueryHref(query.id)}
                  className="flex min-w-0 items-center gap-2 font-medium text-ink no-underline hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DataPointIcon size={14} className="shrink-0 text-ink-4" aria-hidden />
                  <span className="truncate">{query.name}</span>
                </Link>
              </TableCell>
              <TableCell className="max-w-0">
                {query.queryText ? (
                  <span className="block truncate font-mono text-12 text-ink-4">
                    {previewSql(query.queryText)}
                  </span>
                ) : (
                  <span className="text-ink-4">—</span>
                )}
              </TableCell>
              <TableCell>
                {query.databaseName ? (
                  <Badge variant="secondary" className="inline-flex items-center gap-1">
                    <DatabaseIcon size={11} aria-hidden />
                    {query.databaseName}
                  </Badge>
                ) : (
                  <span className="text-ink-4">—</span>
                )}
              </TableCell>
              <TableCell>
                {query.queryType ? (
                  <Badge variant="outline">{query.queryType}</Badge>
                ) : (
                  <span className="text-ink-4">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/** One-line SQL preview — collapse whitespace so multi-line SQL fits a row. */
function previewSql(sql: string): string {
  return sql.replace(/\s+/g, ' ').trim()
}

function subtitle(total: number): string {
  if (total === 0) {
    return 'Named queries and query data points across your ingested gateways and databases.'
  }
  return `${total} ${total === 1 ? 'query' : 'queries'} across named queries and query data points.`
}

export default QueriesPage
